import { api } from '@setplex/tria-api'
import { attach, sample } from 'effector'
import { createForm } from 'effector-forms'
import { FROM, passwordSecurity } from '~/features/password-security'
import {
  email,
  equalTo,
  maxLength,
  minLength,
  password,
  required,
  username,
} from '~/shared/validators'

// sign up request effect
export const signUpFx = attach({ effect: api.subscriber.signUpFx })

// sign up form
export const signUpForm = createForm({
  fields: {
    email: {
      init: '' as string,
      rules: [required(), email()],
    },
    username: {
      init: '' as string,
      rules: [required(), username(), maxLength.validator(80)],
    },
    password: {
      init: '' as string,
      rules: [
        required(),
        {
          name: maxLength.name,
          source: passwordSecurity.$policy,
          validator: (value, _ignore, policy) => {
            const targetPolicy = policy[FROM.FEATURES]
            return maxLength.validator(targetPolicy.max).validator(value)
          },
        },
        {
          name: password.name,
          source: passwordSecurity.$policy,
          validator: (value, _ignore, policy) => {
            const targetPolicy = policy[FROM.FEATURES]
            return password.validator(targetPolicy.regex).validator(value)
          },
        },
        {
          name: minLength.name,
          source: passwordSecurity.$policy,
          validator: (value, _ignore, policy) => {
            const targetPolicy = policy[FROM.FEATURES]
            return minLength.validator(targetPolicy.min).validator(value)
          },
        },
      ],
    },
    confirm: {
      init: '' as string,
      rules: [required(), equalTo('password')],
    },
  },
  validateOn: ['submit', 'blur'],
})

// send sign-up request
sample({
  clock: signUpForm.formValidated,
  fn: ({ email, username, password }) => ({
    email: email.trim(),
    username: username.trim(),
    password,
  }),
  target: signUpFx,
})

//
// handle dynamic fields errors (~ server side validation)
//

// WG0024 - Email is not valid
sample({
  clock: api.events.logic.emailNotValid,
  fn: () => ({ rule: 'invalid' }),
  target: signUpForm.fields.email.addError,
})

// WG0034 - Password is not valid
sample({
  clock: api.events.logic.passwordNotValid,
  fn: () => ({ rule: 'invalid' }),
  target: signUpForm.fields.password.addError,
})

// WG0075 - Username is not unique
sample({
  clock: api.events.logic.usernameNotUnique,
  fn: () => ({ rule: 'unique' }),
  target: signUpForm.fields.username.addError,
})

// WG0073 - Subscriber username is required
sample({
  clock: api.events.logic.subscriberUsernameRequired,
  fn: () => ({ rule: 'required' }),
  target: signUpForm.fields.username.addError,
})

// WG0074 - Username doesn't correspond regexp pattern
sample({
  clock: api.events.logic.usernameNotNotMatchRE,
  fn: () => ({ rule: 'invalid' }),
  target: signUpForm.fields.username.addError,
})

// WG0076 - Username is too long, maximum length is 80 characters
sample({
  clock: api.events.logic.usernameTooLong,
  fn: () => ({ rule: 'maxLength' }),
  target: signUpForm.fields.username.addError,
})

// WG0078 - Subscriber password is required
sample({
  clock: api.events.logic.subscriberPasswordRequired,
  fn: () => ({ rule: 'required' }),
  target: signUpForm.fields.password.addError,
})

// WG0079 - Password is too long, maximum length is 64 characters
sample({
  clock: api.events.logic.passwordTooLong,
  fn: () => ({ rule: 'maxLength' }),
  target: signUpForm.fields.password.addError,
})

// WG0080 - Password is too short, minimum allowed length is 6 characters
sample({
  clock: api.events.logic.passwordTooShort,
  fn: () => ({ rule: 'minLength' }),
  target: signUpForm.fields.password.addError,
})

// WG0081 - Password doesn't correspond regexp pattern
sample({
  clock: api.events.logic.passwordNotMatchRE,
  fn: () => ({ rule: 'invalid' }),
  target: signUpForm.fields.password.addError,
})
